import { path } from 'ramda'
import { useState, useEffect } from 'react'
import { Helmet } from 'react-helmet-async'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import styled from 'styled-components'
import ContentWrapper from '@/components/content-wrapper'
import Page from '@/components/page'
import PageTitle from '@/components/page-title'
import AreaTitle from '@/components/area-title'
import { DefaultButtonBar } from '@/components/button-bar'
import useMediaQuery from '@/hooks/use-media-query'
import useBasic from '@/hooks/use-basic'
import useProfile from '@/hooks/use-profile'
import { colors, fontWeights, breakpoints, structure } from '@/styles'
import { toUnit } from '@/utils/styles'
import { getFormattedCountries } from '@/utils/address'
import View from './view'
import Edit from './edit'

const StyledPage = styled(Page)`
  height: calc(100vh - ${toUnit(structure.header.height)});
`

const UpperProfile = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
`

const EditButton = styled.button`
  height: 28px;
  min-width: 68px;
  border: 1px solid ${colors.mediumGrey};
  border-radius: 2px;
  background-color: transparent;
  cursor: pointer;
  margin-bottom: 20px;
  color: ${colors.secondary};
  font-size: 12px;
  font-weight: ${fontWeights.medium};
  line-height: 12px;
  text-align: center;
`

const BillingAddress = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const [isEditing, setIsEditing] = useState(false)
  const userProfile = useProfile()
  const hasAddress = userProfile.has_billing_address
  const addressData = useSelector(path(['settlements', 'billing_address']))
  const { countries } = useBasic(['countries'])
  const formattedCountries = getFormattedCountries(countries)
  const isMobile = useMediaQuery(`(max-width: ${breakpoints.ipadMiniMax})`)

  useEffect(() => {
    if (hasAddress && !addressData) dispatch.settlements.getBillingAddress()
  }, [hasAddress, addressData, dispatch.settlements])

  const editButtonText = hasAddress
    ? t('common.edit', 'Edit')
    : `+ ${t('r.settings.view.add_billing_address.button', 'Add a Billing Address')}`

  return (
    <StyledPage>
      <Helmet>
        <title>{t('b.settings.view.account_settings.title', 'Account Settings')}</title>
      </Helmet>
      <PageTitle>{t('b.settings.view.account_settings.title', 'Account Settings')}</PageTitle>
      <Page>
        <ContentWrapper className="mb-50" padding={'40px'}>
          <UpperProfile>
            <AreaTitle bottom={16}>
              {t('r.settings.view.billing_address.title', 'Billing Address')}
            </AreaTitle>
            {!isEditing && (
              <EditButton type="button" onClick={() => setIsEditing(true)}>
                {editButtonText}
              </EditButton>
            )}
          </UpperProfile>
          {isEditing ? (
            <Edit
              setIsEditing={setIsEditing}
              hasAddress={hasAddress}
              addressData={addressData}
              formattedCountries={formattedCountries}
            />
          ) : (
            <View
              hasAddress={hasAddress}
              addressData={addressData}
              formattedCountries={formattedCountries}
            />
          )}
        </ContentWrapper>
        {!isMobile && <DefaultButtonBar />}
      </Page>
    </StyledPage>
  )
}

export default BillingAddress
